// Status surfaces for the QE/TE map view (T13): the truthful truncation banner and the zero-features
// empty overlay. Split out of SpatialResultsMapPanel so that file stays ≤200 lines. Presentational
// only — the pure derivation lives in `spatialResultsMapView.ts`. No maplibre import.

/** Amber truncation banner — renders nothing when there is nothing to say. */
export function SpatialResultsMapBanner({ parts }: { parts: string[] }) {
  if (parts.length === 0) return null;
  return (
    <div
      className="flex-shrink-0 border-b border-amber-500/30 bg-amber-500/10 px-2 py-1 text-[11px] text-amber-700 dark:text-amber-300"
      data-testid="spatial-map-truncation-banner"
    >
      {parts.join(' · ')}
    </div>
  );
}

/** Empty overlay shown when zero features decode — never a silently blank canvas.
 * `title` overrides the default headline (T14's TE pane passes "No rows in the
 * current window" when the loaded window is empty). */
export function SpatialResultsMapEmpty({
  nullCount,
  failedCount,
  tooLargeCount,
  title = 'No mappable geometries in this result',
}: {
  nullCount: number;
  failedCount: number;
  tooLargeCount: number;
  title?: string;
}) {
  const parts: string[] = [];
  if (nullCount > 0) parts.push(`${nullCount.toLocaleString()} null`);
  if (failedCount > 0) parts.push(`${failedCount.toLocaleString()} failed`);
  if (tooLargeCount > 0) parts.push(`${tooLargeCount.toLocaleString()} too large`);
  return (
    <div
      className="flex h-full w-full items-center justify-center p-4 text-center"
      data-testid="spatial-map-empty"
    >
      <div className="text-xs text-muted-foreground">
        <div className="font-medium">{title}</div>
        {parts.length > 0 && <div className="mt-1">{parts.join(' · ')}</div>}
      </div>
    </div>
  );
}
